import {areSetsEqual} from '@benchling/js-utils';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {getRenderedGridRows} from 'studies/typed/shared/procedure-runs/conditions/conditionGroupGridRows';
import type {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import type {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {
  getProcedureRunDesignFlowchartNodeId,
  parseProcedureRunDesignFlowchartNodeId,
} from './ProcedureRunDesignFlowchartNodeIdUtils';

export interface UseProcedureRunDesignNodeRemovalEditsResult {
  /** The set of node IDs currently tracked as removed in this edit session. */
  removedNodeIds: ReadonlySet<string>;
  /** Add newly removed node IDs into the tracked draft removal state. Used on confirmation of node removal. */
  updateRemovedNodeIds: (nodeIds: ReadonlySet<string>) => void;
  /** Clear all tracked node removals, for example when canceling edits. */
  resetNodeRemovals: () => void;
}

const KEY_SEPARATOR = '::';

/** Durable key for one flowchart node staged for removal, stable across condition reorders. */
export function getPendingRemovedNodeKey(conditionId: string, replicateNumber: number): string {
  return `${conditionId}${KEY_SEPARATOR}${replicateNumber}`;
}

/** Maps current-index flowchart node ids to durable pending-removal keys, skipping draft nodes. */
export function convertNodeIdsToPendingRemovedNodeKeys(
  nodeIds: ReadonlySet<string>,
  conditions: ReadonlyArray<ConditionGroup>
): ReadonlySet<string> {
  const pendingRemovedNodeKeys = new Set<string>();
  for (const nodeId of nodeIds) {
    const {conditionIndex, replicateIndex, methodId} = parseProcedureRunDesignFlowchartNodeId(nodeId);
    const conditionId = conditions[conditionIndex]?.conditionIdsByPmdvId[methodId];
    if (conditionId == null) {
      continue;
    }
    pendingRemovedNodeKeys.add(getPendingRemovedNodeKey(conditionId, replicateIndex + 1));
  }
  return pendingRemovedNodeKeys;
}

/** Keys for every currently-live node in the rendered grid. */
function collectLiveNodeKeys(
  conditions: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<ExperimentMethod>
): ReadonlySet<string> {
  const liveNodeKeys = new Set<string>();
  getRenderedGridRows(conditions, methods, false).forEach(({conditionGroupIndex, replicateNumber, cells}) => {
    const condition = conditions[conditionGroupIndex];
    if (condition == null) {
      return;
    }
    for (const {methodId} of cells) {
      const conditionId = condition.conditionIdsByPmdvId[methodId];
      if (conditionId != null) {
        liveNodeKeys.add(getPendingRemovedNodeKey(conditionId, replicateNumber));
      }
    }
  });
  return liveNodeKeys;
}

/**
 * Derives current-index flowchart node ids from durable pending-removal keys so reordering cannot point
 * removals at the wrong row. Archived or missing nodes are omitted.
 */
export function deriveRemovedNodeIdsFromPendingRemovals(
  pendingRemovedNodeKeys: ReadonlySet<string>,
  conditions: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<ExperimentMethod>
): Set<string> {
  const derived = new Set<string>();
  if (pendingRemovedNodeKeys.size === 0) {
    return derived;
  }

  getRenderedGridRows(conditions, methods, false).forEach(
    ({conditionGroupIndex, replicateIndex, replicateNumber, cells}) => {
      const condition = conditions[conditionGroupIndex];
      if (condition == null) {
        return;
      }
      for (const {methodId} of cells) {
        const conditionId = condition.conditionIdsByPmdvId[methodId];
        if (conditionId == null) {
          continue;
        }
        if (pendingRemovedNodeKeys.has(getPendingRemovedNodeKey(conditionId, replicateNumber))) {
          derived.add(
            getProcedureRunDesignFlowchartNodeId({
              conditionIndex: conditionGroupIndex,
              replicateIndex,
              methodId,
            })
          );
        }
      }
    }
  );
  return derived;
}

/**
 * React hook that owns temporary removed-node state across edit, save, and refetch operations.
 *
 * Removals are stored as durable pending-removal keys and projected to current-index node ids each
 * render so table write-backs cannot point them at the wrong row. A pending removal whose node leaves
 * the live grid (archived on save, or an unsaved replicate dropped by lowering the replicate count) is
 * pruned so it cannot resurrect onto a later node that reuses the same condition and replicate number.
 *
 * @param conditions Current conditions.
 * @param methods Current methods.
 * @returns Helpers and state for tracking removed nodes (see {@link UseProcedureRunDesignNodeRemovalEditsResult}).
 */
export function useProcedureRunDesignNodeRemovalEdits(
  conditions: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<ExperimentMethod>
): UseProcedureRunDesignNodeRemovalEditsResult {
  const [pendingRemovedNodeKeys, setPendingRemovedNodeKeys] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    setPendingRemovedNodeKeys((current) => {
      if (current.size === 0) {
        return current;
      }
      const liveNodeKeys = collectLiveNodeKeys(conditions, methods);
      const pruned = new Set([...current].filter((key) => liveNodeKeys.has(key)));
      return pruned.size === current.size ? current : pruned;
    });
  }, [conditions, methods]);

  const derivedRemovedNodeIds = useMemo(
    () => deriveRemovedNodeIdsFromPendingRemovals(pendingRemovedNodeKeys, conditions, methods),
    [pendingRemovedNodeKeys, conditions, methods]
  );

  // Keep a stable Set reference when the derived node ids are unchanged.
  const derivedRemovedNodeIdsRef = useRef(derivedRemovedNodeIds);
  if (!areSetsEqual(derivedRemovedNodeIds, derivedRemovedNodeIdsRef.current)) {
    derivedRemovedNodeIdsRef.current = derivedRemovedNodeIds;
  }

  const updateRemovedNodeIds = useCallback(
    (nodeIds: ReadonlySet<string>) => {
      setPendingRemovedNodeKeys((current) => {
        const converted = convertNodeIdsToPendingRemovedNodeKeys(nodeIds, conditions);
        const next = new Set([...current, ...converted]);
        return next.size === current.size ? current : next;
      });
    },
    [conditions]
  );

  const resetNodeRemovals = useCallback(() => {
    setPendingRemovedNodeKeys(new Set());
  }, []);

  return {
    removedNodeIds: derivedRemovedNodeIdsRef.current,
    updateRemovedNodeIds,
    resetNodeRemovals,
  };
}
